/**
 * NotificationsPage — full inbox view.
 *
 * - Lists every notification for the current user (newest first, up to 200).
 * - Filter tabs: All / Unread / Forum / Learning / Announcements.
 * - Bulk actions: mark all read, delete.
 */
import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import {
  Bell, MessageSquare, BookOpen, Megaphone, Beaker, Info,
  CheckCheck, Trash2, ArrowLeft, Filter,
} from 'lucide-react';
import ToolSelector from '../components/ToolSelector';
import ThemeToggle from '../components/ThemeToggle';
import BrandMark from '../components/BrandMark';
import PageHero from '../components/PageHero';
import PushNotificationSettings from '../components/PushNotificationSettings';

const iconFor = (n) => {
  if (n?.type === 'forum_reply' || n?.type === 'forum_mention') return MessageSquare;
  if (n?.type === 'lesson_added' || n?.type === 'course_updated') return BookOpen;
  if (n?.type === 'admin_broadcast') return Megaphone;
  if (n?.type === 'batch_milestone') return Beaker;
  return Info;
};

const FILTERS = [
  { key: 'all',           label: 'All',           match: () => true },
  { key: 'unread',        label: 'Unread',        match: (n) => !n.is_read },
  { key: 'forum',         label: 'Forum',         match: (n) => (n.type || '').startsWith('forum_') },
  { key: 'learning',      label: 'Learning',      match: (n) => n.type === 'lesson_added' || n.type === 'course_updated' },
  { key: 'announcements', label: 'Announcements', match: (n) => n.type === 'admin_broadcast' },
];

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleString();
};

const NotificationsPage = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications`, { params: { limit: 200 } });
      setItems(data?.items || []);
    } catch (e) {
      toast.error('Could not load notifications');
    }
    setLoading(false);
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    return items.filter(f.match);
  }, [items, filter]);

  const unreadCount = useMemo(() => items.filter((n) => !n.is_read).length, [items]);

  const handleClickItem = async (n) => {
    if (!n.is_read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      try { await axios.put(`${API}/notifications/${n.id}/read`); } catch (_) { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      toast.success('All notifications marked read');
    } catch (_) {
      toast.error('Failed to mark all read');
    }
  };

  const handleDelete = async (n, e) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    try { await axios.delete(`${API}/notifications/${n.id}`); } catch (_) { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="notifications-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/80 hover:text-foreground"
            data-testid="notif-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <BrandMark size="md" onClick={() => navigate('/dashboard')} />
          <ThemeToggle />
        </div>
      </header>

      <ToolSelector />

      <PageHero
        eyebrow="Inbox"
        eyebrowIcon={Bell}
        title={<>Your <span className="text-amber-brand">notifications</span></>}
        description="Forum replies, new lessons, batch milestones and academy announcements — all in one place."
        testId="notifications-hero"
      />

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <PushNotificationSettings />

        <div className="flex items-center justify-between flex-wrap gap-3 mb-4 mt-6">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" role="tablist">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            {FILTERS.map((f) => {
              const active = f.key === filter;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors flex-shrink-0 ${active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground/80 hover:border-primary/60'}`}
                  data-testid={`notif-filter-${f.key}`}
                  role="tab"
                  aria-selected={active}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAll}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition-colors"
              data-testid="notif-mark-all"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          {loading && (
            <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-14 flex flex-col items-center text-center gap-3" data-testid="notif-empty">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Bell className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="text-base font-extrabold text-foreground">Nothing to see here</div>
              <div className="text-sm text-muted-foreground max-w-md">
                Once someone replies to your thread, a new lesson is added to a course you follow, or admin sends an announcement, it will show up here.
              </div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <ul className="divide-y divide-border" data-testid="notif-list">
              {filtered.map((n) => {
                const Icon = iconFor(n);
                return (
                  <li key={n.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleClickItem(n)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClickItem(n); } }}
                      className={`w-full text-left px-5 py-4 hover:bg-muted/40 transition-colors flex items-start gap-4 cursor-pointer ${!n.is_read ? 'bg-primary/[.04]' : ''}`}
                      data-testid={`notif-row-${n.id}`}
                    >
                      <div className={`h-10 w-10 rounded-xl flex-shrink-0 flex items-center justify-center ${!n.is_read ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-extrabold text-foreground flex items-center gap-2">
                          <span className="truncate">{n.title}</span>
                          {!n.is_read && <span className="inline-block h-2 w-2 rounded-full bg-primary flex-shrink-0" />}
                        </div>
                        {n.body && <div className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground/80 mt-1.5">
                          {(n.type || 'system').replace(/_/g, ' ')} · {relTime(n.created_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDelete(n, e)}
                        className="text-muted-foreground hover:text-destructive p-1 rounded-md transition-colors flex-shrink-0"
                        aria-label="Dismiss"
                        data-testid={`notif-delete-${n.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
